import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useEventStream, type StreamState } from "../api/eventStreamContext";
import { useRuntimeStatus } from "../api/runtimeStatusContext";
import { formatAgo, shorten } from "../lib/format";
import type { Status } from "../types";

type Tone = "neutral" | "good" | "warn" | "bad";
type StatusDestination = "history" | "settings";

interface Summary {
  label: string;
  tone: Tone;
  /** Only the transport grace is transient; every finding of the HTTP status shows at once. */
  transient?: boolean;
}

interface StatusIssue {
  label: string;
  tone: "warn" | "bad";
  priority: number;
}

const stateText: Record<string, string> = {
  idle: "on demand",
  ready: "ready",
  healthy: "healthy",
  fallback: "HTTP fallback",
  degraded: "degraded",
  connecting: "connecting",
  unconfigured: "not configured",
  stale: "stale",
  offline: "offline",
  locked: "locked",
  recovering: "recovering",
  unavailable: "unavailable",
  unknown: "unknown",
};

function componentTone(state: string): Tone {
  if (state === "idle") return "neutral";
  if (state === "ready" || state === "healthy") return "good";
  if (
    state === "fallback" ||
    state === "degraded" ||
    state === "stale" ||
    state === "locked" ||
    state === "connecting"
  )
    return "warn";
  return "bad";
}

function streamPresentation(state: StreamState) {
  if (state === "connected") return { label: "connected", tone: "good" as const };
  if (state === "connecting" || state === "reconnecting") {
    return { label: state, tone: "warn" as const };
  }
  if (state === "stale") return { label: "heartbeat stale", tone: "bad" as const };
  return { label: "offline", tone: "bad" as const };
}

function issueForState(
  state: string,
  labels: { unavailable: string; warning: string },
  priority: number,
): StatusIssue | null {
  const tone = componentTone(state);
  if (tone === "good" || tone === "neutral") return null;
  return {
    label: tone === "bad" ? labels.unavailable : labels.warning,
    tone,
    priority: tone === "bad" ? priority : priority + 100,
  };
}

function healthText(health: Status["rpc"]["websocket"]) {
  if (!health) return "unknown";
  return stateText[health.state] ?? health.state;
}

function statusSummary({
  freshness,
  recoveryRequired,
  streamState,
  rpcState,
  catalogState,
  executorState,
  attentionCount,
  recoveryPending,
  recoveryGrace,
}: {
  freshness: "fresh" | "stale" | "unknown";
  recoveryRequired: boolean;
  streamState: StreamState;
  rpcState: string;
  catalogState: string;
  executorState: string;
  attentionCount: number;
  recoveryPending: boolean;
  recoveryGrace: boolean;
}): Summary {
  const issues: StatusIssue[] = [];
  if (freshness === "fresh") {
    if (recoveryRequired) return { label: "Recovery required", tone: "bad" };
    const rpcIssue = issueForState(
      rpcState,
      {
        unavailable: "RPC unavailable",
        warning:
          rpcState === "stale"
            ? "RPC stale"
            : rpcState === "fallback"
              ? "RPC fallback"
              : "RPC degraded",
      },
      20,
    );
    if (rpcIssue) issues.push(rpcIssue);
    const catalogIssue = issueForState(
      catalogState,
      { unavailable: "Catalog unavailable", warning: "Catalog stale" },
      30,
    );
    if (catalogIssue) issues.push(catalogIssue);
    const executorIssue = issueForState(
      executorState,
      {
        unavailable:
          executorState === "recovering" ? "Executor recovering" : "Executor unavailable",
        warning: "Executor locked",
      },
      5,
    );
    if (executorIssue) issues.push(executorIssue);
    if (attentionCount > 0) {
      issues.push({ label: "Operations need attention", tone: "warn", priority: 150 });
    }
    issues.sort((left, right) => left.priority - right.priority);
    if (issues[0]) return issues[0];
  }
  if (recoveryPending && recoveryGrace) {
    return { label: "Reconnecting", tone: "warn", transient: true };
  }
  if (freshness === "unknown") return { label: "Server status unavailable", tone: "bad" };
  if (freshness === "stale") return { label: "Server status stale", tone: "bad" };

  if (recoveryPending) {
    return {
      label: streamState === "disconnected" ? "Events offline" : "Events stale",
      tone: "bad",
    };
  }

  const stream = streamPresentation(streamState);
  if (stream.tone === "bad") {
    return {
      label: stream.label === "offline" ? "Events offline" : "Events stale",
      tone: "bad",
    };
  }
  if (stream.tone === "warn") {
    return {
      label: stream.label === "connecting" ? "Events connecting" : "Events reconnecting",
      tone: "warn",
    };
  }
  return { label: "All systems normal", tone: "good" };
}

export function GlobalStatus({
  onNavigate,
}: {
  onNavigate?: (destination: StatusDestination) => void;
}) {
  const stream = useEventStream();
  const status = useRuntimeStatus();
  const value = status.data;
  const live = status.freshness === "fresh";
  const unavailableState = status.freshness === "stale" ? "stale" : "unknown";
  const rpcState =
    live && value
      ? value.rpc.state === "degraded" && value.rpc.fallback?.state === "healthy"
        ? "fallback"
        : value.rpc.state
      : unavailableState;
  const catalogState = live && value ? value.catalog.state : unavailableState;
  const executorState = live && value ? value.executor.state : unavailableState;
  const attentionCount = live ? (value?.operationsNeedingAttention ?? 0) : 0;
  const summary = statusSummary({
    freshness: status.freshness,
    recoveryRequired: value?.recoveryRequired === true,
    streamState: stream.state,
    rpcState,
    catalogState,
    executorState,
    attentionCount,
    recoveryPending: status.recoveryPending === true,
    recoveryGrace: status.recoveryGrace === true,
  });
  // A tab return reconnects under the hood: for the first seconds the pill keeps what the user last
  // saw, so a healthy reconnect never flashes "Reconnecting". Hidden renders have no authority.
  const shown = useRef<{ summary: Summary; attentionCount: number } | null>(null);
  if (status.active !== false && !summary.transient) shown.current = { summary, attentionCount };
  const pill =
    status.recoveryQuiet === true && summary.transient && shown.current
      ? shown.current
      : { summary, attentionCount };
  const [open, setOpen] = useState(false);
  const popoverId = useId();
  const control = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let frame = 0;
    const show = () => {
      setOpen(true);
      frame = window.requestAnimationFrame(() => trigger.current?.focus());
    };
    window.addEventListener("auto-pools:show-system-status", show);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("auto-pools:show-system-status", show);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const closeOnPointer = (event: PointerEvent) => {
      if (!control.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener("pointerdown", closeOnPointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnPointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const actualBlockingWarning =
    live && value?.recoveryRequired === true
      ? "Recovery and reconciliation are required."
      : status.freshness === "stale"
        ? "Server HTTP status is stale, so execution state is unknown."
        : status.freshness === "fresh"
          ? value?.executor.state === "locked"
            ? "The executor is locked."
            : value?.executor.state === "recovering"
              ? "The executor is recovering."
              : value?.executor.state === "unavailable"
                ? "The executor is unavailable."
                : null
          : "Server HTTP status is unavailable, so execution state is unknown.";
  const graceVisible =
    status.recoveryPending === true &&
    status.recoveryGrace === true &&
    !(live && value?.recoveryRequired === true) &&
    !(live && ["locked", "recovering", "unavailable"].includes(value?.executor.state ?? ""));
  const blockingWarning = graceVisible ? null : actualBlockingWarning;
  const streamStatus = streamPresentation(stream.state);
  const lastSnapshot = value ? `Last snapshot: ${formatAgo(value.panel.updatedAt)}` : "No data yet";

  function navigate(destination: StatusDestination) {
    setOpen(false);
    onNavigate?.(destination);
  }

  return (
    <section className="global-status" aria-label="System status" aria-live="polite">
      <div className="status-control" ref={control}>
        <button
          ref={trigger}
          className={`status-trigger ${pill.summary.tone}`}
          type="button"
          aria-expanded={open}
          aria-controls={popoverId}
          onClick={() => {
            setOpen((current) => !current);
          }}
        >
          <span className={`status-dot ${pill.summary.tone}`} />
          <span>{pill.summary.label}</span>
          {pill.attentionCount > 0 && <b className="attention-badge">{pill.attentionCount}</b>}
          <svg aria-hidden="true" viewBox="0 0 14 14">
            <path d={open ? "m3.5 8.5 3.5-3.5 3.5 3.5" : "m3.5 5.5 3.5 3.5 3.5-3.5"} />
          </svg>
        </button>
        {open && (
          <div
            className="status-popover"
            id={popoverId}
            role="dialog"
            aria-label="System status details"
          >
            <header>
              <strong>System status</strong>
              <span>{value ? `checked ${formatAgo(value.panel.updatedAt)}` : "not checked"}</span>
            </header>
            <div className="status-list">
              <StatusItem
                name="Events"
                state={streamStatus.label}
                tone={streamStatus.tone}
                detail={
                  stream.lastEventAt
                    ? `Last event ${formatAgo(stream.lastEventAt)}`
                    : "No event received yet"
                }
              />
              <StatusItem
                name="RPC / WS"
                state={stateText[rpcState] ?? rpcState}
                tone={componentTone(rpcState)}
                detail={
                  live && value
                    ? rpcState === "idle"
                      ? `Last RPC read: ${formatAgo(value.rpc.lastCheckedAt)}`
                      : `WS: ${healthText(value.rpc.websocket)} · fallback: ${healthText(value.rpc.fallback)} · last RPC read: ${formatAgo(value.rpc.lastCheckedAt)}`
                    : lastSnapshot
                }
              />
              <StatusItem
                name="Catalog"
                state={
                  catalogState === "idle"
                    ? value?.catalog.lastCheckedAt
                      ? "On demand"
                      : "Upstream not checked"
                    : (stateText[catalogState] ?? catalogState)
                }
                tone={componentTone(catalogState)}
                detail={
                  live && value
                    ? catalogState === "idle"
                      ? value.catalog.lastCheckedAt
                        ? `Last loaded ${formatAgo(value.catalog.lastCheckedAt)}`
                        : "Catalog loads on request"
                      : `Checked ${formatAgo(value.catalog.lastCheckedAt)}`
                    : lastSnapshot
                }
              />
              <StatusItem
                name="Executor"
                state={stateText[executorState] ?? executorState}
                tone={componentTone(executorState)}
                detail={
                  value?.wallet.address
                    ? shorten(value.wallet.address)
                    : value?.wallet.configured
                      ? "Wallet configured"
                      : "Wallet not configured"
                }
                action={
                  executorState !== "ready" ? (
                    <button
                      type="button"
                      onClick={() => {
                        navigate("settings");
                      }}
                    >
                      Settings →
                    </button>
                  ) : null
                }
              />
            </div>
            {(attentionCount > 0 || (!live && (value?.operationsNeedingAttention ?? 0) > 0)) && (
              <footer>
                <b className="attention-badge">
                  {live
                    ? attentionCount
                    : `Last known: ${String(value?.operationsNeedingAttention)}`}
                </b>
                <span>{live ? "operations need attention" : "operations needed attention"}</span>
                <button
                  type="button"
                  onClick={() => {
                    navigate("history");
                  }}
                >
                  Open in History →
                </button>
              </footer>
            )}
          </div>
        )}
      </div>
      {blockingWarning && (
        <div role="alert" className="status-banner">
          <svg aria-hidden="true" viewBox="0 0 16 16">
            <circle cx="8" cy="8" r="6.5" />
            <path d="M8 4.8v3.6M8 11v.1" />
          </svg>
          <span>
            <strong>New operations are blocked.</strong> {blockingWarning}
          </span>
          {status.freshness === "fresh" ? (
            <button
              type="button"
              onClick={() => {
                navigate("settings");
              }}
            >
              Settings
            </button>
          ) : (
            <button type="button" onClick={status.reload}>
              Retry
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function StatusItem({
  name,
  state,
  tone,
  detail,
  action,
}: {
  name: string;
  state: string;
  tone: Tone;
  detail: string;
  action?: ReactNode;
}) {
  return (
    <div className={`status-item ${tone}`}>
      <span className={`status-dot ${tone}`} />
      <span>
        <strong>{name}</strong>
        <small>{detail}</small>
      </span>
      <span className="status-item-state">
        {state}
        {action}
      </span>
    </div>
  );
}
